import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartAddEvent.eventName, this.#syncEmptyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#syncEmptyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartAddEvent.eventName, this.#syncEmptyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#syncEmptyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      // Prevent opening if already open or if dialog is in transition
      const { dialog } = this.refs;
      if (dialog && !dialog.open && !dialog.classList.contains('dialog-closing')) {
        this.showDialog();
      }
    }
  };

  open() {
    this.showDialog();
    // GA4 view_cart tracking
    this.#trackViewCart();
    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }
 #trackViewCart() {
    // Fetch cart data
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('Cart data fetched:', cart);
        
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({ ecommerce: null }); // Clear previous ecommerce data
        
        // const items = cart.items.map(item => ({
        //   item_id: item.product_id.toString(),
        //   item_name: item.product_title,
        //   item_brand: item.vendor || '',
        //   item_category: item.product_type || '',
        //   item_variant: item.variant_id.toString(),
        //   price: (item.final_price / 100).toFixed(2),
        //   currency: cart.currency,
        //   quantity: item.quantity
        // }));
        const items = cart.items.map((item, index) => ({
          item_id: item.product_id.toString(),
          item_name: item.product_title,
          item_brand: item.vendor || '',
          item_category: item.product_type || '',
          item_category2: '',
          item_category3: '',
          item_category4: '',
          item_category5: '',
          item_list_name: '',
          item_variant: item.variant_id.toString(),
          price: (item.final_price / 100).toFixed(2),
          currency: cart.currency,
          quantity: item.quantity,
          index: index,
          discount: item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0 
            ? (item.line_level_discount_allocations.reduce((sum, discount) => sum + discount.amount, 0) / 100).toFixed(2) 
            : 0,
          coupon: ''
        }));

        
        dataLayer.push({
          event: 'view_cart',
          ecommerce: {
            currency: cart.currency,
            value: (cart.total_price / 100).toFixed(2),
            items: items
          }
        });
        
        console.log('view_cart event pushed to dataLayer', JSON.stringify({
          event: 'view_cart',
          ecommerce: {
            currency: cart.currency,
            value: (cart.total_price / 100).toFixed(2),
            items: items
          }
        }));
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
      });
  }
  close() {
    this.closeDialog();
  }

  #syncEmptyState = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    const applyState = () => {
      const count = Number(dialog.querySelector('[ref="cartItemCount"]')?.textContent?.trim() || '0');
      dialog.classList.toggle('cart-drawer--empty', count === 0);
    };

    // Run on next frames to ensure morph updates are reflected.
    requestAnimationFrame(applyState);
    setTimeout(applyState, 0);
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
